import { useState, useRef, useEffect } from 'react'
import { XMarkIcon, ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

interface DigitalSignatureProps {
    isOpen: boolean
    onClose: () => void
    onSave: (signatureUrl: string) => void
    title?: string
}

export default function DigitalSignature({
    isOpen,
    onClose,
    onSave,
    title = "Sign Post Order"
}: DigitalSignatureProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [isDrawing, setIsDrawing] = useState(false)
    const [hasSignature, setHasSignature] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [lastX, setLastX] = useState(0)
    const [lastY, setLastY] = useState(0)

    // Function to get stroke color based on current theme
    const getStrokeColor = () => {
        const isDarkMode = document.documentElement.classList.contains('dark')
        return isDarkMode ? '#ffffff' : '#1f2937'
    }

    // Function to update canvas stroke color
    const updateStrokeColor = () => {
        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d')
            if (ctx) {
                ctx.strokeStyle = getStrokeColor()
            }
        }
    }

    useEffect(() => {
        if (isOpen && canvasRef.current) {
            const canvas = canvasRef.current
            const ctx = canvas.getContext('2d')
            if (ctx) {
                // Set canvas size
                canvas.width = canvas.offsetWidth
                canvas.height = canvas.offsetHeight

                // Set drawing style with appropriate color
                ctx.strokeStyle = getStrokeColor()
                ctx.lineWidth = 3
                ctx.lineCap = 'round'
                ctx.lineJoin = 'round'

                // Clear canvas
                ctx.clearRect(0, 0, canvas.width, canvas.height)
                setHasSignature(false)
            }
        }
    }, [isOpen])

    // Listen for theme changes and update stroke color
    useEffect(() => {
        const observer = new MutationObserver(() => {
            updateStrokeColor()
        })

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        })

        return () => observer.disconnect()
    }, [])

    const getMousePos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current!
        const rect = canvas.getBoundingClientRect()

        if ('touches' in e) {
            // Touch event
            const touch = e.touches[0]
            return {
                x: touch.clientX - rect.left,
                y: touch.clientY - rect.top
            }
        } else {
            // Mouse event
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            }
        }
    }

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault()
        const pos = getMousePos(e)
        setLastX(pos.x)
        setLastY(pos.y)
        setIsDrawing(true)
    }

    const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault()
        if (!isDrawing) return

        const canvas = canvasRef.current!
        const ctx = canvas.getContext('2d')!
        const pos = getMousePos(e)

        ctx.beginPath()
        ctx.moveTo(lastX, lastY)
        ctx.lineTo(pos.x, pos.y)
        ctx.stroke()

        setLastX(pos.x)
        setLastY(pos.y)
        setHasSignature(true)
    }

    const stopDrawing = () => {
        setIsDrawing(false)
    }

    const clearSignature = () => {
        const canvas = canvasRef.current!
        const ctx = canvas.getContext('2d')!
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        // Reset stroke color after clearing
        ctx.strokeStyle = getStrokeColor()
        setHasSignature(false)
    }

    const saveSignature = async () => {
        if (!hasSignature) {
            toast.error('Please draw your signature first')
            return
        }

        try {
            setIsSaving(true)

            const canvas = canvasRef.current!

            // Convert canvas to blob
            const blob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((blob) => {
                    resolve(blob!)
                }, 'image/png', 0.9)
            })

            // Create file from blob
            const file = new File([blob], `signature-${Date.now()}.png`, {
                type: 'image/png'
            })

            // Create FormData
            const formData = new FormData()
            formData.append('signature', file)

            // Upload to server
            const LOCAL_API_URL = 'http://localhost:17000/api/app'
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL || 'https://api.connecttechusa.com/api/app'}/post-orders/upload-signature`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                    },
                    body: formData
                }
            )

            if (!response.ok) {
                throw new Error('Failed to upload signature')
            }

            const result = await response.json()

            if (result.data?.url) {
                onSave(result.data.url)
                toast.success('Signature saved successfully')
                onClose()
            } else {
                throw new Error('No signature URL received')
            }
        } catch (error) {
            console.error('Error saving signature:', error)
            toast.error('Failed to save signature')
        } finally {
            setIsSaving(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-tech-900 rounded-lg shadow-xl w-full max-w-md">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-tech-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-tech-50">
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg bg-gray-100 dark:bg-tech-700 text-gray-600 dark:text-tech-300 hover:bg-gray-200 dark:hover:bg-tech-600 transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Signature Area */}
                <div className="p-4">
                    <div className="mb-4">
                        <p className="text-sm text-gray-600 dark:text-tech-400 mb-2">
                            Draw your signature below:
                        </p>
                        <div className="border-2 border-dashed border-gray-300 dark:border-tech-600 rounded-lg bg-white dark:bg-tech-900 relative">
                            <canvas
                                ref={canvasRef}
                                className="w-full h-48 cursor-crosshair touch-none bg-white dark:bg-tech-900"
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                            {!hasSignature && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <p className="text-sm text-gray-400 dark:text-tech-500 italic">
                                        Tap and drag to sign
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-3">
                        <button
                            onClick={clearSignature}
                            disabled={!hasSignature}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 text-gray-700 dark:text-tech-300 border border-gray-300 dark:border-tech-700 rounded-lg hover:bg-gray-50 dark:hover:bg-tech-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <ArrowPathIcon className="w-4 h-4" />
                            <span>Clear</span>
                        </button>
                        <button
                            onClick={saveSignature}
                            disabled={!hasSignature || isSaving}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isSaving ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <CheckIcon className="w-4 h-4" />
                            )}
                            <span>{isSaving ? 'Saving...' : 'Save Signature'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
